import { Anchor, Stack } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { DateCell, TextCell } from "@tfg/ui"
import _ from "lodash"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { OperationCountriesCell } from "~/components/cells/OperationCountriesCell.tsx"
import { ProductTraceabilityListItemResponseDto } from "~/gen/api"
import { TraceabilityStatusCell } from "~/pages/Brand/Products/List/Components/TraceabilityStatusCell"

const columnHelper =
  createColumnHelper<ProductTraceabilityListItemResponseDto>()

const operationRankAccessor =
  (rank: number) =>
  (
    row: ProductTraceabilityListItemResponseDto
  ): LibraryResponses.OperationNode[] => {
    if (!row.operationTree) return []
    return row.operationTree.nodes.filter(
      (node) => node.rank === rank
    ) as LibraryResponses.OperationNode[]
  }

export const useProductsTraceabilityColumns = (
  maxRank: number,
  showOnlyMainFabric: boolean
) => {
  const navigate = useNavigate()
  const { t } = useTranslation(["products"])

  return useMemo<ColumnDef<ProductTraceabilityListItemResponseDto, any>[]>(
    () => [
      columnHelper.accessor("traceability.status", {
        id: "traceabilityStatus",
        header: () => (
          <TextCell
            value={t("lists.columns.traceabilityStatus.label")}
            tooltip={t("lists.columns.traceabilityStatus.tooltip")}
            fw="var(--table-header-text-weight)"
          />
        ),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => (
          <TraceabilityStatusCell
            traceabilityStatus={info.row.original.traceability.status}
          />
        ),
      }),
      columnHelper.accessor("traceability.traceabilityLaunchedAt", {
        id: "traceabilityLaunchedAt",
        header: () => (
          <TextCell
            value={t("lists.columns.traceabilityLaunchedAt.label")}
            tooltip={t("lists.columns.traceabilityLaunchedAt.tooltip")}
            fw="var(--table-header-text-weight)"
          />
        ),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => <DateCell date={info.getValue()} format="DD/MM/YYYY" />,
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: t("lists.columns.name"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => (
          <Anchor
            fz="xs"
            c="gray.9"
            onClick={() =>
              navigate(`/products/details/${info.row.original.id}`)
            }
          >
            <TextCell value={info.getValue()} />
          </Anchor>
        ),
      }),
      columnHelper.accessor("brandReference", {
        id: "brandReference",
        header: t("lists.columns.reference"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("purchaseOrderReference", {
        id: "purchaseOrder",
        header: t("lists.columns.purchaseOrder"),
        enableSorting: false,
        cell: (info) => (
          <TextCell
            value={info.row.original.purchaseOrderReference ?? undefined}
          />
        ),
      }),
      columnHelper.accessor("color", {
        header: t("lists.columns.color"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("brandType", {
        id: "type",
        header: t("lists.columns.type"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("collection", {
        header: t("lists.columns.collection"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue() ?? undefined} />,
      }),
      ..._.times(maxRank + 1, (i) =>
        columnHelper.accessor(operationRankAccessor(i), {
          id: `rank-${i}`,
          size: 300,
          minSize: 200,
          maxSize: 400,
          header: t("lists.columns.rank", { rank: i + 1 }),
          enableSorting: false,
          cell: (info) => {
            return (
              <Stack h="100%" py={4}>
                <OperationCountriesCell
                  operationNodes={info.getValue()}
                  showOnlyMainFabric={showOnlyMainFabric}
                />
              </Stack>
            )
          },
        })
      ),
    ],
    [t, maxRank, showOnlyMainFabric, navigate]
  )
}
